/** The route table. Most specific first: `matchRoutes` takes the first hit. */
import type { PageContext, Route, Teardown } from './router';
import { renderLandingPage } from './pages/landing';
import { renderOwnerLinkPage } from './pages/owner-link';
import { renderCalendarPage } from './pages/calendar';
import { renderViewerPage } from './pages/viewer';
import { renderCollectPage } from './pages/collect';
import { renderSettingsPage } from './pages/settings';
import { renderAdminPage } from './pages/admin';
import { errorState } from '../ui/states';
import { rememberInviteCode } from '../ui/invite-create';
import { el } from '../ui/dom';

const home: Route = { pattern: '/', render: renderLandingPage };

/**
 * `/i/<code>` is an invitation. It puts the code where the create form will
 * find it and then renders whatever `/` renders, so the visitor lands on the
 * front door with the box already filled in rather than on a page of its own.
 */
const invitation: Route = {
  pattern: '/i/:code',
  render: (ctx) => {
    const code = ctx.params.code;
    if (code) rememberInviteCode(code);
    return home.render(ctx);
  },
};

/**
 * `/cal/:id` was the ring's address before the rename, and links to it are out
 * in the world. The worker keeps only `/cal/<icsToken>.ics` for itself and
 * hands everything else under the prefix to the SPA, so an old link lands here
 * and is swapped for the new one in place: `replaceState`, not a push, so the
 * dead address does not sit in history waiting for the Back button. The search
 * string and the hash carry over — `?view=` and the camera both live there.
 */
const legacyCalendar: Route = {
  pattern: '/cal/:id',
  render: (ctx) => {
    const id = encodeURIComponent(ctx.params.id!);
    ctx.navigate(`/circles/${id}${location.search}`, { replace: true, keepHash: true });
  },
};

export const routes: Route[] = [
  home,
  // The same page, with the manual already open at "How it works".
  { pattern: '/about', render: renderLandingPage },
  invitation,
  { pattern: '/settings', render: renderSettingsPage },
  // The operator's page: the deployment's numbers and its invitations, behind
  // the admin token rather than any account.
  { pattern: '/admin', render: renderAdminPage },
  { pattern: '/o/:calendarId/:ownerToken', render: renderOwnerLinkPage },
  { pattern: '/circles/:id', render: renderCalendarPage },
  legacyCalendar,
  { pattern: '/c/:token', render: renderViewerPage },
  { pattern: '/collect/:token', render: renderCollectPage },
];

export function renderNotFound(ctx: PageContext): Teardown {
  ctx.host.appendChild(
    errorState({
      title: 'Nothing at this address',
      description: 'The link may be incomplete, or the page has moved.',
      actions: [el('a', { class: 'btn', href: '/', text: 'Go to your calendars' })],
    }),
  );
}
